import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Collapse,
  IconButton,
  Stack,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  ExpandLess,
  ExpandMore,
} from '@mui/icons-material';

import { useTheme } from '../../../theme/ThemeProvider';
import { ThemeVisualSelector } from './ThemeVisualSelector';
import type { 
  ThemeSettingsControllerProps, 
  ThemeMode 
} from '../../../constants/interfaces/themeSettings';
import { useTranslation } from '../../../services/translation';

export const ThemeSettingsController: React.FC<ThemeSettingsControllerProps> = ({
  value,
  onChange,
  compact = false,
  title = 'theme_settings',
  onEvent,
}) => {
  const { t } = useTranslation();
  const themeContext = useTheme();
  const [isExpanded, setIsExpanded] = useState(!compact);

  // Use controlled value or context
  const currentSettings = value || themeContext.settings;
  const handleSettingsChange = onChange || themeContext.updateSettings;

  const handleThemeChange = (newMode: ThemeMode) => {
    // Log the theme change event
    onEvent?.({
      type: 'theme_mode_change',
      value: newMode,
      previousMode: currentSettings.mode
    });

    handleSettingsChange({
      ...currentSettings,
      mode: newMode,
    });
  };

  // Reset handler removed as it wasn't used

  return (
    <Card 
      elevation={compact ? 0 : 1}
      sx={{ 
        borderRadius: { xs: 1, sm: 2 },
        overflow: 'hidden',
        width: '100%',
        '& .MuiCardContent-root': {
          '&:last-child': { pb: compact ? 2 : 3 }
        }
      }}
    >
      <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
        {/* Header Section */}
        <Box 
          display="flex" 
          alignItems="center" 
          justifyContent="space-between" 
          mb={compact ? 2 : 3}
          pb={compact ? 0 : 1.5}
          borderBottom={compact ? 'none' : '1px solid'}
          borderColor="divider"
        >
          <Box display="flex" alignItems="center" gap={1}>
            <Box
              sx={(theme) => ({
                p: { xs: 0.75, sm: 1 },
                borderRadius: 1.5,
                backgroundColor: 'primary.main',
                color: 'primary.contrastText',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                '& .MuiSvgIcon-root': {
                  fill: `${theme.palette.primary.contrastText} !important`,
                  color: `${theme.palette.primary.contrastText} !important`,
                  fontSize: { xs: '1rem', sm: '1.25rem' }
                }
              })}
            >
              <SettingsIcon fontSize="small" />
            </Box>
            <Box>
              <Typography 
                sx={{ fontSize: 'calc(16px * var(--unicity-font-scale, 1))', fontWeight: 600 }} 
              >
                {t(title)}
              </Typography>
              {!compact && (
                <Typography 
                  sx={{ color: 'text.secondary', mt: 0.25, fontSize: 'calc(12px * var(--unicity-font-scale, 1))' }}
                >
                  {t('choose_your_preferred_theme')}
                </Typography>
              )}
            </Box>
          </Box>
          {compact && (
            <IconButton 
              onClick={() => setIsExpanded(!isExpanded)}
              size="small"
              sx={{ 
                border: '1px solid',
                borderColor: 'divider',
                '&:hover': { borderColor: 'primary.main' }
              }}
            >
              {isExpanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          )}
        </Box>

        <Collapse in={isExpanded}>
          <Stack spacing={compact ? 2 : 3}>
            {/* Integrated Theme Selection */}
            <ThemeVisualSelector
              currentMode={currentSettings.mode}
              onModeChange={handleThemeChange}
            />
          </Stack>
        </Collapse>
      </CardContent>
    </Card>
  );
};